import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import {
  InterchangeError,
  SHEET_FORMAT_ID,
  SheetEvaluator,
  TEMPLATE_FORMAT_ID,
  builtInTemplates,
  formatOf,
  readSheet,
  readTemplate,
  slugify,
  writeTemplate,
  type SheetTemplate,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../../db/client.ts';
import { realmFile } from '../../db/protocol.ts';
import { saveBytes } from '../../files.ts';
import { settings } from '../../settings.ts';
import { NewSheet } from './NewSheet.tsx';

/**
 * Character sheets (docs/PARITY-STEP-2-PLAN.md section 5), as the desktop's Tools > Character
 * Sheets: the realm's sheets and templates, New sheet from a template, New template (blank, or
 * from a built-in), and Import and Export of either as JSON.
 */

const route = getRouteApi('/_app/realm/$realmKey/sheets');

/** Character sheets and templates are JSON files, named for what's in them. */
export function saveJson(json: string, name: string) {
  saveBytes(new TextEncoder().encode(json), `${slugify(name) || 'sheet'}.json`, 'application/json');
}

export const message = (e: unknown, fallback: string) => (e instanceof RealmClientError || e instanceof InterchangeError ? e.message : fallback);

const MAX_FILE = 2 * 1024 * 1024;

export function SheetsPage() {
  const { sheets, templates, entryTitles } = route.useLoaderData();
  const { realmKey } = route.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<'none' | 'new' | 'templates'>('none');
  const [deleting, setDeleting] = useState<{ kind: 'sheet' | 'template'; id: string; name: string } | null>(null);
  const [error, setError] = useState('');
  const byId = new Map(templates.map((t) => [t.id, t]));
  const used = (template: SheetTemplate) => sheets.filter((s) => s.templateId === template.id).length;

  const openSheet = (sheetId: string) => void navigate({ to: '/realm/$realmKey/sheets/$sheetId', params: { realmKey, sheetId } });
  const openTemplate = (templateId: string) => void navigate({ to: '/realm/$realmKey/templates/$templateId', params: { realmKey, templateId } });

  async function act(work: () => Promise<void>, fallback: string) {
    setError('');
    try {
      await work();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  async function importFile(chosen: File | undefined) {
    if (!chosen) return;
    await act(async () => {
      if (chosen.size > MAX_FILE) throw new InterchangeError('That file is too large to be a sheet or a template (over 2 MB).');
      const json = await chosen.text();
      const format = formatOf(json);
      if (format === SHEET_FORMAT_ID) openSheet((await realms.importSheet(file, readSheet(json))).id);
      else if (format === TEMPLATE_FORMAT_ID) openTemplate((await realms.importTemplate(file, readTemplate(json))).id);
      else throw new InterchangeError(`${chosen.name} isn't a Realmery character sheet or template.`);
    }, 'The file could not be imported.');
  }

  type Deleting = { kind: 'sheet' | 'template'; id: string; name: string };

  async function remove({ kind, id }: Deleting) {
    setDeleting(null);
    await act(async () => {
      if (kind === 'sheet') await realms.deleteSheet(file, id);
      else await realms.deleteTemplate(file, id);
      await router.invalidate();
    }, `The ${kind} could not be deleted.`);
  }

  function askDelete(kind: 'sheet' | 'template', id: string, name: string) {
    if (settings()['general.confirmDeletes']) setDeleting({ kind, id, name });
    else void remove({ kind, id, name });
  }

  return (
    <article className="document sheets-page">
      <p className="eyebrow">Character sheets</p>
      <h1>Character sheets</h1>
      <p className="lead">Sheets for the realm's characters and creatures, from a template: 5th edition, Fate Accelerated, Forged in the Dark, or one of your own.</p>
      <div className="actions">
        <button type="button" className="primary" onClick={() => setPanel(panel === 'new' ? 'none' : 'new')}>
          New sheet
        </button>
        <button type="button" aria-expanded={panel === 'templates'} onClick={() => setPanel(panel === 'templates' ? 'none' : 'templates')}>
          New template…
        </button>
        <label className="button file-button">
          Import…
          <input type="file" accept=".json,application/json" hidden onChange={(e) => void importFile(e.currentTarget.files?.[0]).then(() => (e.target.value = ''))} />
        </label>
      </div>
      {panel === 'new' && <NewSheet file={file} forEntry={null} onCreated={openSheet} onCancel={() => setPanel('none')} />}
      {panel === 'templates' && (
        <div className="panel" role="group" aria-label="New template">
          <p className="meta">Design a template of your own: blank, or starting from a built-in one.</p>
          <div className="actions">
            <button type="button" onClick={() => void act(async () => openTemplate((await realms.createTemplate(file, 'New Template')).id), 'The template could not be made.')}>
              Blank template
            </button>
            {builtInTemplates().map((builtIn) => (
              <button key={builtIn.key} type="button" className="quiet" onClick={() => void act(async () => openTemplate((await realms.templateFromBuiltIn(file, builtIn.key)).id), 'The template could not be made.')}>
                Start from {builtIn.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {deleting && (
        <div className="panel confirm" role="alertdialog" aria-label={`Delete ${deleting.name}`}>
          <p>{deleting.kind === 'sheet' ? `Delete “${deleting.name}”'s sheet? Its entry stays.` : `Delete the template “${deleting.name}”?`}</p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => void remove(deleting)}>
              Delete {deleting.kind}
            </button>
            <button type="button" onClick={() => setDeleting(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      <h2>Sheets</h2>
      {sheets.length === 0 ? (
        <p className="meta">No character sheets yet. New sheet makes one from a template, and any Person or Creature entry can have a sheet from its own page.</p>
      ) : (
        <ul className="card-gallery">
          {sheets.map((sheet) => {
            const template = byId.get(sheet.templateId);
            const summary = template ? new SheetEvaluator(template, sheet.values).summary() : '';
            return (
              <li key={sheet.id} className="card">
                <Link className="card-title" to="/realm/$realmKey/sheets/$sheetId" params={{ realmKey, sheetId: sheet.id }}>
                  {sheet.name}
                </Link>
                <span className="meta">{template?.name}</span>
                {sheet.entryId && entryTitles[sheet.entryId] && <span className="meta">↳ {entryTitles[sheet.entryId]}</span>}
                {summary && <span className="meta">{summary}</span>}
                <div className="actions">
                  <button type="button" className="quiet" title="Delete this sheet (its entry stays)" onClick={() => askDelete('sheet', sheet.id, sheet.name)}>
                    Delete…
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <h2>Templates</h2>
      {templates.length > 0 && (
        <ul className="card-gallery">
          {templates.map((template) => {
            const count = used(template);
            return (
              <li key={template.id} className="card">
                <Link className="card-title" to="/realm/$realmKey/templates/$templateId" params={{ realmKey, templateId: template.id }}>
                  {template.name}
                </Link>
                <span className="meta">
                  {template.system ? `${template.system} · ` : ''}
                  {count} {count === 1 ? 'sheet' : 'sheets'}
                </span>
                <div className="actions">
                  <button type="button" className="quiet" title="Make a copy of this template to change" onClick={() => void act(async () => openTemplate((await realms.duplicateTemplate(file, template.id)).id), 'The template could not be copied.')}>
                    Duplicate
                  </button>
                  <button type="button" className="quiet" title="Save this template as a JSON file to share" onClick={() => saveJson(writeTemplate(template), template.name)}>
                    Export
                  </button>
                  <Link className="button quiet" to="/realm/$realmKey/templates/$templateId/print" params={{ realmKey, templateId: template.id }} title="Print a blank sheet from this template, or save it as a PDF, to fill in by hand">
                    Blank sheet
                  </Link>
                  <button type="button" className="quiet" disabled={count > 0} title={count > 0 ? 'Sheets still use this template' : 'Delete this template'} onClick={() => askDelete('template', template.id, template.name)}>
                    Delete…
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="meta">Built-in templates are copied into this realm the first time a sheet uses one; New template can also start from any of them.</p>
    </article>
  );
}
